import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { http, money, errMsg, imgSrc } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const NICK_RE = /^[A-Za-z0-9_.]{2,20}$/;

const METHODS = [
  { id: "pix", name: "Pix", sub: "Brasil · pagamento em R$", cur: "BRL", ring: "border-emerald-500 bg-emerald-500/10", dot: "bg-emerald-500" },
  { id: "mbway", name: "MB Way", sub: "Portugal · pagamento em €", cur: "EUR", ring: "border-rose-500 bg-rose-500/10", dot: "bg-rose-500" },
];

export default function Checkout() {
  const { cart, total, clear, currency, setCurrency } = useStore();
  const nav = useNavigate();
  const [method, setMethod] = useState(currency === "EUR" ? "mbway" : "pix");
  const [form, setForm] = useState({ customer_name: "", email: "", nickname: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [nickBad, setNickBad] = useState(false);
  const m = METHODS.find((x) => x.id === method);
  const set = (k) => (e) => { if (k === "nickname") setNickBad(false); setForm({ ...form, [k]: e.target.value }); };

  if (cart.length === 0)
    return <div className="max-w-3xl mx-auto px-8 py-24 text-center text-slate-400" data-testid="checkout-empty">Seu carrinho está vazio. <Link to="/" className="text-violet-400">Ver itens</Link></div>;

  const submit = async (e) => {
    e.preventDefault();
    if (!NICK_RE.test(form.nickname.trim())) { setNickBad(true); toast.error("Verifique o seu nick do Minecraft"); return; }
    setNickBad(false);
    setBusy(true);
    try {
      const r = await http.post("/orders", { ...form, nickname: form.nickname.trim(), payment_method: method, items: cart.map((i) => ({ product_id: i.id, quantity: i.quantity })) });
      clear();
      nav(`/pedido/${r.data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-5 gap-10" data-testid="checkout-form">
      <div className="lg:col-span-3 space-y-8">
        <h1 className="font-heading font-extrabold text-4xl tracking-tight">Finalizar compra</h1>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2"><Label>Nome</Label><Input required value={form.customer_name} onChange={set("customer_name")} data-testid="checkout-name-input" className="h-11 bg-white/5" /></div>
          <div className="space-y-2"><Label>E-mail</Label><Input required type="email" value={form.email} onChange={set("email")} data-testid="checkout-email-input" className="h-11 bg-white/5" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Observações (opcional)</Label><Textarea value={form.notes} onChange={set("notes")} data-testid="checkout-notes-input" className="bg-white/5" /></div>
        </div>
        <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/5 p-5 space-y-2" data-testid="checkout-nick-box">
          <Label className="font-heading font-bold text-base text-emerald-300">Seu nick no Minecraft</Label>
          <Input required value={form.nickname} onChange={set("nickname")}
            placeholder="ex: Steve_123" data-testid="checkout-nickname-input" className="h-12 bg-[#0B0F19] font-mono text-lg" />
          {nickBad && <p className="text-sm text-rose-400" data-testid="checkout-nickname-error">Nick inválido: use 2 a 20 caracteres (letras, números, _ ou .), sem espaços.</p>}
          <p className="text-xs text-slate-400">Escreva exatamente como aparece no jogo. É para este nick que o servidor vai enviar os itens após a confirmação do pagamento.</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">Forma de pagamento</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {METHODS.map((x) => (
              <button type="button" key={x.id} data-testid={`checkout-payment-method-${x.id}`}
                onClick={() => { setMethod(x.id); setCurrency(x.cur); }}
                className={`text-left p-5 rounded-2xl border-2 transition-colors ${method === x.id ? x.ring : "border-white/10 bg-white/5 hover:border-white/25"}`}>
                <div className="flex items-center gap-3"><span className={`w-3 h-3 rounded-full ${x.dot}`} /><span className="font-heading font-bold text-lg">{x.name}</span></div>
                <p className="text-sm text-slate-400 mt-1">{x.sub}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
      <aside className="lg:col-span-2 rounded-3xl bg-[#111827] border border-white/10 p-6 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
        <p className="font-heading font-bold text-lg">Resumo</p>
        <div className="space-y-3 mt-5">
          {cart.map((i) => (
            <div key={i.id} className="flex items-center gap-3 text-sm">
              <img src={imgSrc(i.image)} alt="" className="w-10 h-10 rounded-md object-cover" />
              <span className="flex-1 truncate">{i.quantity}× {i.name}</span>
              <span className="font-mono">{money((m.cur === "BRL" ? i.price_brl : i.price_eur) * i.quantity, m.cur)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 mt-5 pt-5 flex justify-between items-baseline">
          <span className="text-slate-400">Total</span>
          <span className="font-mono text-2xl font-bold" data-testid="checkout-total">{money(total(m.cur), m.cur)}</span>
        </div>
        <button disabled={busy} data-testid="checkout-submit-button"
          className="w-full mt-6 h-12 rounded-full bg-violet-600 hover:bg-violet-500 transition-colors font-semibold disabled:opacity-50">
          {busy ? "Criando pedido..." : `Fazer pedido e pagar com ${m.name}`}
        </button>
      </aside>
    </form>
  );
}
